
import Vue from 'vue';
import moment from 'moment-timezone';
import _isNil from 'lodash/isNil';
import { dataTableItemsOrderByPagination } from '@/utils/dataTables';

export default Vue.extend({
	name: 'TableBulkActionsPopover',
	props: {
		headers: { type: Array, required: true },
		items: { type: Array, required: true },
		hasDownload: { type: Boolean, default: false },
		downloadFileName: { type: String, default: 'table-data' },
		/**
		 * Ends the file name with the moment of the download (`-YYYYMMDD-HHmmss`), so
		 * downloading again never gives a file the same name as the last one. On for every
		 * table; pass false for a name that never changes.
		 */
		timestampDownload: { type: Boolean, default: true },
		pagination: { type: Object, default: null },
		/** When false, hide default slot and its leading divider (e.g. SCIM locks membership actions). */
		showActionSlot: { type: Boolean, default: true },
		/**
		 * Steps the bar aside while something else holds the space above the table (a list's open
		 * Filters box, which it would cover). The selection is kept, so the bar comes back with it.
		 */
		hidden: { type: Boolean, default: false },
		/**
		 * What the CSV is built from, when the table's own items would not read well in it
		 * (a list of teams, a role that is only an id). Read by header `value`, like `items`.
		 * Left out, the CSV is built from `items`.
		 */
		downloadItems: { type: Array, default: null },
		/**
		 * The CSV's columns, when they are not the table's: `{ text, value }` each, read from the
		 * items by `value` like a header. Left out, the CSV has the table's columns.
		 */
		downloadHeaders: { type: Array, default: null },
	},
    computed: {
        sanitizedDownloadFileName () {
            return this.downloadFileName.replace(/[^a-zA-Z0-9]/g, '-');
        },
    },
	methods: {
		downloadCsv() {
			// Filter out action columns
			const columns: { text: string; value: string }[] = (this.downloadHeaders || this.headers)
				.filter(({ noDownload }) => !noDownload);
			// A value with a comma, a quote or a line break is quoted, its quotes doubled,
			// or it would spill into the next column or row
			const csvCell = (value: any): string => {
				const stringValue: string = String(value);
				return /[",\r\n]/.test(stringValue) ?
					`"${stringValue.replace(/"/g, '""')}"` :
					stringValue;
			};

			// Convert data to CSV rows
			// Sort items before download
			const sortedItems: any[] = dataTableItemsOrderByPagination(this.downloadItems || this.items, this.pagination);
			const csvRows: string[] = sortedItems.map((item: any) => {
				return columns
					.map(({ value }) => csvCell(value && !_isNil(this._get(item, value)) ? this._get(item, value) : ''))
					.join(',');
			});

			// Combine headers and rows
			const csvContent: string = [
				columns.map(({ text }) => csvCell(text)).join(','),
				...csvRows
			].join('\n');

			// Create and trigger download
			const blob: Blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
			const link: HTMLAnchorElement = document.createElement('a');
			link.href = URL.createObjectURL(blob);
			// Stamped at the click, never in a computed: a cached name would repeat on the next download
			const stamp: string = this.timestampDownload ? `-${moment().format('YYYYMMDD-HHmmss')}` : '';
			link.setAttribute('download', `${this.sanitizedDownloadFileName}${stamp}.csv`);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		},
	},
});
